(() => {
  const STORAGE_KEY = "md_quote_v1";

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function normaliseQuantity(value) {
    const quantity = Number.parseInt(value, 10);

    if (!Number.isFinite(quantity) || quantity < 1) {
      return 1;
    }

    return Math.min(quantity, 9999);
  }

  function formatMoney(value) {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return "";
    }

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount);
  }

  function loadQuote() {
    try {
      const storedValue = localStorage.getItem(STORAGE_KEY);

      if (!storedValue) {
        return { items: [] };
      }

      const parsed = JSON.parse(storedValue);

      if (!parsed || !Array.isArray(parsed.items)) {
        return { items: [] };
      }

      return {
        ...parsed,
        items: parsed.items
          .filter((item) => item && item.variantId)
          .map((item) => ({
            ...item,
            variantId: String(item.variantId),
            qty: normaliseQuantity(item.qty),
          })),
      };
    } catch (error) {
      console.error("Could not read quote:", error);
      return { items: [] };
    }
  }

  function saveQuote(quote) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...quote,
          items: Array.isArray(quote.items) ? quote.items : [],
        })
      );

      window.dispatchEvent(
        new CustomEvent("medisa:quote-updated", {
          detail: quote,
        })
      );
    } catch (error) {
      console.error("Could not save quote:", error);
    }
  }

  function getSelectedOptionMap(productElement) {
    const selected = {};

    productElement
      .querySelectorAll("[data-product-option-select]")
      .forEach((select) => {
        selected[select.dataset.optionName] = select.value;
      });

    return selected;
  }

  function variantMatchesOptions(variant, selectedOptions) {
    const variantOptions = Array.isArray(variant.selectedOptions)
      ? variant.selectedOptions
      : [];

    return Object.entries(selectedOptions).every(
      ([optionName, optionValue]) =>
        variantOptions.some(
          (option) =>
            String(option.name) === String(optionName) &&
            String(option.value) === String(optionValue)
        )
    );
  }

  function findVariantById(product, variantId) {
    return product.variants.find(
      (variant) =>
        String(variant.variantId) === String(variantId)
    );
  }

  function initialiseQuotePage(root) {
    if (!root || root.dataset.quotePageInitialised === "true") {
      return;
    }

    root.dataset.quotePageInitialised = "true";

    const searchEndpoint = root.dataset.searchEndpoint;
    const submitEndpoint = root.dataset.submitEndpoint;

    const searchForm = root.querySelector(
      "[data-quote-search-form]"
    );
    const searchInput = root.querySelector(
      "[data-quote-search-input]"
    );
    const searchButton = root.querySelector(
      "[data-quote-search-button]"
    );
    const searchButtonText = root.querySelector(
      "[data-quote-search-button-text]"
    );
    const searchMessage = root.querySelector(
      "[data-quote-search-message]"
    );

    const resultsSection = root.querySelector(
      "[data-quote-results-section]"
    );
    const resultsSummary = root.querySelector(
      "[data-quote-results-summary]"
    );
    const resultsEmpty = root.querySelector(
      "[data-quote-results-empty]"
    );
    const resultsLoading = root.querySelector(
      "[data-quote-results-loading]"
    );
    const resultsList = root.querySelector(
      "[data-quote-results-list]"
    );

    const quoteEmpty = root.querySelector("[data-quote-empty]");
    const quoteItems = root.querySelector("[data-quote-items]");
    const quoteTotals = root.querySelector("[data-quote-totals]");
    const quoteItemCount = root.querySelector(
      "[data-quote-item-count]"
    );
    const quoteTotalQuantity = root.querySelector(
      "[data-quote-total-quantity]"
    );
    const clearButton = root.querySelector("[data-quote-clear]");

const submitForm = root.querySelector(
  "[data-quote-submit-form]"
);

const submitButton = root.querySelector(
  "[data-quote-submit]"
);

const submitButtonText = root.querySelector(
  "[data-quote-submit-text]"
);

const submitMessage = root.querySelector(
  "[data-quote-submit-message]"
);

    let quote = loadQuote();
    let activeSearchController = null;
    let automaticSearchTimer = null;
let lastRenderedSearchKey = "";
let isSubmitting = false;

const searchCache = new Map();
const AUTO_SEARCH_DELAY = 2000;

    function showMessage(element, message, type = "") {
      if (!element) return;

      element.textContent = message;
      element.hidden = false;
      element.classList.remove("is-error", "is-success");

      if (type === "error") {
        element.classList.add("is-error");
      }

      if (type === "success") {
        element.classList.add("is-success");
      }
    }

    function hideMessage(element) {
      if (!element) return;

      element.hidden = true;
      element.textContent = "";
      element.classList.remove("is-error", "is-success");
    }

    function setSearchLoading(isLoading) {
      if (searchButton) {
        searchButton.disabled = isLoading;
      }

      if (searchButtonText) {
        searchButtonText.textContent = isLoading
          ? "Searching..."
          : "Search";
      }

      if (resultsLoading) {
        resultsLoading.hidden = !isLoading;
      }

      if (isLoading) {
        if (resultsEmpty) resultsEmpty.hidden = true;
        if (resultsList) resultsList.hidden = true;
      }
    }

    function findQuoteItem(variantId) {
      return quote.items.find(
        (item) =>
          String(item.variantId) === String(variantId)
      );
    }

    function updateProductCard(productElement) {
      const product = productElement.quoteProductData;

      if (!product) return;

      const selectedOptions =
        getSelectedOptionMap(productElement);

      const selectedVariant = product.variants.find((variant) =>
        variantMatchesOptions(variant, selectedOptions)
      );

      const variantTitleElement = productElement.querySelector(
        "[data-selected-variant-title]"
      );
      const skuElement = productElement.querySelector(
        "[data-selected-sku]"
      );
      const mpnElement = productElement.querySelector(
        "[data-selected-mpn]"
      );
      const priceElement = productElement.querySelector(
        "[data-selected-price]"
      );
      const availabilityElement = productElement.querySelector(
        "[data-selected-availability]"
      );
      const addButton = productElement.querySelector(
        "[data-add-product-variant]"
      );

      productElement.selectedVariantData =
        selectedVariant || null;

      if (!selectedVariant) {
        if (variantTitleElement) {
          variantTitleElement.textContent =
            "This option combination is not available.";
        }

        if (skuElement) {
          skuElement.hidden = true;
        }

        if (mpnElement) {
          mpnElement.hidden = true;
        }

        if (priceElement) {
          priceElement.textContent = "";
        }

        if (availabilityElement) {
          availabilityElement.textContent = "Unavailable";
          availabilityElement.className =
            "md-quote-result__badge md-quote-result__badge--unavailable";
        }

        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = "Unavailable";
          delete addButton.dataset.variantId;
        }

        return;
      }

      if (variantTitleElement) {
        variantTitleElement.textContent =
          selectedVariant.variantTitle || "";
      }

      if (skuElement) {
        skuElement.hidden = !selectedVariant.sku;
        skuElement.textContent = selectedVariant.sku
          ? `SKU: ${selectedVariant.sku}`
          : "";
      }

      if (mpnElement) {
        mpnElement.hidden = !selectedVariant.mpn;
        mpnElement.textContent = selectedVariant.mpn
          ? `MPN: ${selectedVariant.mpn}`
          : "";
      }

      if (priceElement) {
        priceElement.textContent = formatMoney(
          selectedVariant.price
        );
      }

      if (availabilityElement) {
        availabilityElement.textContent =
          selectedVariant.availableForSale
            ? "Available"
            : "Unavailable";

        availabilityElement.className =
          selectedVariant.availableForSale
            ? "md-quote-result__badge md-quote-result__badge--available"
            : "md-quote-result__badge md-quote-result__badge--unavailable";
      }

      if (addButton) {
        addButton.dataset.variantId =
          selectedVariant.variantId;

        const existingItem = findQuoteItem(
          selectedVariant.variantId
        );

        if (!selectedVariant.availableForSale) {
          addButton.disabled = true;
          addButton.textContent = "Unavailable";
          addButton.classList.remove(
            "md-quote-button--secondary"
          );
          addButton.classList.add(
            "md-quote-button--primary"
          );
        } else if (existingItem) {
          addButton.disabled = false;
          addButton.textContent = "Added";
          addButton.classList.remove(
            "md-quote-button--primary"
          );
          addButton.classList.add(
            "md-quote-button--secondary"
          );
        } else {
          addButton.disabled = false;
          addButton.textContent = "Add to quote";
          addButton.classList.remove(
            "md-quote-button--secondary"
          );
          addButton.classList.add(
            "md-quote-button--primary"
          );
        }
      }
    }

    function updateAllProductCards() {
      resultsList
        ?.querySelectorAll("[data-search-product]")
        .forEach(updateProductCard);
    }

    function renderQuote() {
      const items = Array.isArray(quote.items)
        ? quote.items
        : [];

      const totalQuantity = items.reduce(
        (total, item) =>
          total + normaliseQuantity(item.qty),
        0
      );

      if (quoteItemCount) {
        if (items.length === 0) {
          quoteItemCount.textContent = "No products added";
        } else if (items.length === 1) {
          quoteItemCount.textContent = "1 product added";
        } else {
          quoteItemCount.textContent =
            `${items.length} products added`;
        }
      }

      if (quoteTotalQuantity) {
        quoteTotalQuantity.textContent =
          String(totalQuantity);
      }

      if (clearButton) {
        clearButton.hidden = items.length === 0;
      }

      if (submitButton) {
  submitButton.disabled =
    items.length === 0 || isSubmitting;
}

      if (quoteEmpty) {
        quoteEmpty.hidden = items.length > 0;
      }

      if (quoteItems) {
        quoteItems.hidden = items.length === 0;
      }

      if (quoteTotals) {
        quoteTotals.hidden = items.length === 0;
      }

      if (!quoteItems || items.length === 0) {
        if (quoteItems) {
          quoteItems.innerHTML = "";
        }

        updateAllProductCards();
        return;
      }

      quoteItems.innerHTML = items
        .map((item) => {
          const productTitle =
            item.productTitle ||
            item.title ||
            "Selected product";

          const variantTitle =
            item.variantTitle &&
            item.variantTitle.toLowerCase() !==
              "default title"
              ? item.variantTitle
              : "";

          return `
            <article
              class="md-quote-item"
              data-quote-item
              data-variant-id="${escapeHtml(item.variantId)}"
            >
              <div class="md-quote-item__top">
                <div>
                  <h3 class="md-quote-item__title">
                    ${escapeHtml(productTitle)}
                  </h3>

                  ${
                    variantTitle
                      ? `
                        <p class="md-quote-item__variant">
                          ${escapeHtml(variantTitle)}
                        </p>
                      `
                      : ""
                  }

                  ${
                    item.sku
                      ? `
                        <p class="md-quote-item__sku">
                          SKU: ${escapeHtml(item.sku)}
                        </p>
                      `
                      : ""
                  }
                </div>

                <button
                  type="button"
                  class="md-quote-item__remove"
                  data-remove-quote-item
                  data-variant-id="${escapeHtml(item.variantId)}"
                >
                  Remove
                </button>
              </div>

              <div class="md-quote-item__bottom">
                <div class="md-quote-quantity">
                  <button
                    type="button"
                    class="md-quote-quantity__button"
                    data-quote-quantity-change="-1"
                    data-variant-id="${escapeHtml(item.variantId)}"
                  >
                    −
                  </button>

                  <input
                    class="md-quote-quantity__input"
                    type="number"
                    min="1"
                    max="9999"
                    value="${normaliseQuantity(item.qty)}"
                    data-quote-item-quantity-input
                    data-variant-id="${escapeHtml(item.variantId)}"
                  >

                  <button
                    type="button"
                    class="md-quote-quantity__button"
                    data-quote-quantity-change="1"
                    data-variant-id="${escapeHtml(item.variantId)}"
                  >
                    +
                  </button>
                </div>

                ${
                  Number.isFinite(Number(item.price))
                    ? `
                      <span class="md-quote-item__price">
                        ${escapeHtml(formatMoney(item.price))} each
                      </span>
                    `
                    : ""
                }
              </div>
            </article>
          `;
        })
        .join("");

      updateAllProductCards();
    }

    function saveAndRenderQuote() {
      saveQuote(quote);
      renderQuote();
    }

    function addVariantToQuote(
      product,
      variant,
      quantity
    ) {
      const existingItem = findQuoteItem(
        variant.variantId
      );

      if (existingItem) {
        existingItem.qty =
          normaliseQuantity(existingItem.qty) +
          normaliseQuantity(quantity);
      } else {
        quote.items.push({
          variantId: String(variant.variantId),
          qty: normaliseQuantity(quantity),
          productId: product.productId || "",
          productTitle: product.productTitle || "",
          productHandle: product.productHandle || "",
          variantTitle: variant.variantTitle || "",
          sku: variant.sku || "",
          mpn: variant.mpn || "",
          price: variant.price,
          availableForSale: Boolean(
            variant.availableForSale
          ),
        });
      }

      saveAndRenderQuote();
    }

    function removeQuoteItem(variantId) {
      quote.items = quote.items.filter(
        (item) =>
          String(item.variantId) !== String(variantId)
      );

      saveAndRenderQuote();
    }

    function updateQuoteItemQuantity(
      variantId,
      quantity
    ) {
      const item = findQuoteItem(variantId);

      if (!item) return;

      item.qty = normaliseQuantity(quantity);
      saveAndRenderQuote();
    }

    function buildOptionSelectors(product, defaultVariant) {
      if (
        !Array.isArray(product.options) ||
        product.options.length === 0
      ) {
        return "";
      }

      const defaultOptions = {};

      for (
        const option of defaultVariant?.selectedOptions || []
      ) {
        defaultOptions[option.name] = option.value;
      }

      return `
        <div class="md-quote-result__options">
          ${product.options
            .map((option) => {
              const selectedValue =
                defaultOptions[option.name] ||
                option.values[0] ||
                "";

              return `
                <div class="md-quote-result__option">
                  <label class="md-quote-field__label">
                    ${escapeHtml(option.name)}
                  </label>

                  <select
                    class="md-quote-result__select"
                    data-product-option-select
                    data-option-name="${escapeHtml(option.name)}"
                  >
                    ${option.values
                      .map(
                        (value) => `
                          <option
                            value="${escapeHtml(value)}"
                            ${
                              value === selectedValue
                                ? "selected"
                                : ""
                            }
                          >
                            ${escapeHtml(value)}
                          </option>
                        `
                      )
                      .join("")}
                  </select>
                </div>
              `;
            })
            .join("")}
        </div>
      `;
    }

    function renderSearchResults(products, query) {
      const validProducts = Array.isArray(products)
        ? products
        : [];

      if (!resultsList || !resultsSummary) return;

      if (validProducts.length === 0) {
        resultsList.innerHTML = "";
        resultsList.hidden = true;

        if (resultsEmpty) {
          resultsEmpty.hidden = false;
          resultsEmpty.innerHTML = `
            <div class="md-quote-results__empty-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
              >
                <circle cx="11" cy="11" r="7"></circle>
                <path d="m20 20-3.5-3.5"></path>
              </svg>
            </div>

            <h3>No matching products found</h3>

            <p>
              Check the product name, SKU or MPN and try again.
            </p>
          `;
        }

        resultsSummary.textContent =
          `No results found for “${query}”.`;

        return;
      }

      if (resultsEmpty) {
        resultsEmpty.hidden = true;
      }

      resultsList.hidden = false;

      resultsSummary.textContent =
        validProducts.length === 1
          ? `1 product found for “${query}”.`
          : `${validProducts.length} products found for “${query}”.`;

      resultsList.innerHTML = validProducts
        .map((product, productIndex) => {
          const defaultVariant =
            findVariantById(
              product,
              product.defaultVariantId
            ) ||
            product.variants.find(
              (variant) => variant.availableForSale
            ) ||
            product.variants[0];

          const productUrl = product.productHandle
            ? `/products/${encodeURIComponent(
                product.productHandle
              )}`
            : "";

          return `
  <article
    class="md-quote-result md-quote-result--product"
    data-search-product
    data-product-index="${productIndex}"
  >
    ${
      product.productImage?.url
        ? `
          <a
            class="md-quote-result__image-link"
            href="${escapeHtml(productUrl || "#")}"
            target="_blank"
            rel="noopener"
            aria-label="View ${escapeHtml(product.productTitle)}"
          >
            <img
              class="md-quote-result__image"
              src="${escapeHtml(product.productImage.url)}"
              alt="${escapeHtml(
                product.productImage.altText ||
                product.productTitle ||
                ""
              )}"
              loading="lazy"
              width="${escapeHtml(product.productImage.width || 600)}"
              height="${escapeHtml(product.productImage.height || 600)}"
            >
          </a>
        `
        : `
          <div
            class="md-quote-result__image-placeholder"
            aria-hidden="true"
          >
            No image
          </div>
        `
    }

    <div class="md-quote-result__body">
      <div class="md-quote-result__top">
        <h3 class="md-quote-result__title">
          ${
            productUrl
              ? `
                <a
                  href="${escapeHtml(productUrl)}"
                  target="_blank"
                  rel="noopener"
                >
                  ${escapeHtml(product.productTitle)}
                </a>
              `
              : escapeHtml(product.productTitle)
          }
        </h3>

        <span
          class="md-quote-result__price"
          data-selected-price
        >
          ${escapeHtml(formatMoney(defaultVariant?.price))}
        </span>
      </div>

      <div class="md-quote-result__selection">
        <p
          class="md-quote-result__variant"
          data-selected-variant-title
        >
          ${escapeHtml(defaultVariant?.variantTitle || "")}
        </p>

        <div class="md-quote-result__meta">
          <span
            class="md-quote-result__badge"
            data-selected-sku
            ${defaultVariant?.sku ? "" : "hidden"}
          >
            ${
              defaultVariant?.sku
                ? `SKU: ${escapeHtml(defaultVariant.sku)}`
                : ""
            }
          </span>

          <span
            class="md-quote-result__badge"
            data-selected-mpn
            ${defaultVariant?.mpn ? "" : "hidden"}
          >
            ${
              defaultVariant?.mpn
                ? `MPN: ${escapeHtml(defaultVariant.mpn)}`
                : ""
            }
          </span>

          <span
            class="
              md-quote-result__badge
              ${
                defaultVariant?.availableForSale
                  ? "md-quote-result__badge--available"
                  : "md-quote-result__badge--unavailable"
              }
            "
            data-selected-availability
          >
            ${
              defaultVariant?.availableForSale
                ? "Available"
                : "Unavailable"
            }
          </span>
        </div>
      </div>

      ${buildOptionSelectors(product, defaultVariant)}

      <div class="md-quote-result__purchase">
       <span class="md-quote-result__purchase-label">
    Quantity
  </span>
        <div class="md-quote-quantity">
          <button
            type="button"
            class="md-quote-quantity__button"
            aria-label="Decrease quantity"
            data-result-quantity-change="-1"
          >
            −
          </button>

          <input
            class="md-quote-quantity__input"
            type="number"
            min="1"
            max="9999"
            step="1"
            value="1"
            aria-label="Quantity"
            data-result-quantity
          >

          <button
            type="button"
            class="md-quote-quantity__button"
            aria-label="Increase quantity"
            data-result-quantity-change="1"
          >
            +
          </button>
        </div>

        <button
          type="button"
          class="md-quote-button md-quote-button--primary"
          data-add-product-variant
          data-variant-id="${escapeHtml(
            defaultVariant?.variantId || ""
          )}"
          ${
            defaultVariant?.availableForSale
              ? ""
              : "disabled"
          }
        >
          ${
            defaultVariant?.availableForSale
              ? "Add to quote"
              : "Unavailable"
          }
        </button>
      </div>
    </div>
  </article>`;
        })
        .join("");

      resultsList
        .querySelectorAll("[data-search-product]")
        .forEach((productElement) => {
          const productIndex = Number.parseInt(
            productElement.dataset.productIndex,
            10
          );

          const product = validProducts[productIndex];

          productElement.quoteProductData = product;

          const defaultVariant =
            findVariantById(
              product,
              product.defaultVariantId
            ) ||
            product.variants[0] ||
            null;

          productElement.selectedVariantData =
            defaultVariant;

          updateProductCard(productElement);
        });
    }
function normaliseSearchKey(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function cancelAutomaticSearch() {
  if (!automaticSearchTimer) return;

  window.clearTimeout(automaticSearchTimer);
  automaticSearchTimer = null;
}
    async function searchProducts(query) {
  const trimmedQuery = String(query || "")
    .trim()
    .replace(/\s+/g, " ");

  const searchKey = normaliseSearchKey(trimmedQuery);

  cancelAutomaticSearch();
  hideMessage(searchMessage);

  if (trimmedQuery.length < 2) {
    showMessage(
      searchMessage,
      "Enter at least 2 characters to search.",
      "error"
    );

    searchInput?.focus();
    return;
  }

  /*
   * The currently displayed query has not changed.
   * Do not render again and do not contact the backend.
   */
  if (searchKey === lastRenderedSearchKey) {
    return;
  }

  /*
   * This query was searched previously.
   * Reuse the stored response without another backend request.
   */
  if (searchCache.has(searchKey)) {
    renderSearchResults(
      searchCache.get(searchKey),
      trimmedQuery
    );

    lastRenderedSearchKey = searchKey;

    resultsSection?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    return;
  }

  /*
   * Cancel an older request if the search phrase has changed.
   */
  if (activeSearchController) {
    activeSearchController.abort();
  }

  const requestController = new AbortController();
  activeSearchController = requestController;

  setSearchLoading(true);

  try {
    const url = new URL(searchEndpoint);

    url.searchParams.set("q", trimmedQuery);
    url.searchParams.set("limit", "10");

    const response = await fetch(url.toString(), {
      headers: {
        Accept: "application/json",
      },
      signal: requestController.signal,
    });

    const payload = await response
      .json()
      .catch(() => null);

    if (!response.ok || !payload?.ok) {
      throw new Error(
        payload?.error ||
          `Search failed with status ${response.status}.`
      );
    }

    /*
     * Ignore a response if the user has already changed
     * the input to another phrase.
     */
    const currentInputKey = normaliseSearchKey(
      searchInput?.value
    );

    if (currentInputKey !== searchKey) {
      return;
    }

    const products = Array.isArray(payload.results)
      ? payload.results
      : [];

    searchCache.set(searchKey, products);

    renderSearchResults(products, trimmedQuery);

    lastRenderedSearchKey = searchKey;

    resultsSection?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    console.error("Quote search failed:", error);

    showMessage(
      searchMessage,
      "Product search could not be completed. Please try again.",
      "error"
    );
  } finally {
    /*
     * Do not let an older request clear the loading state
     * of a newer request.
     */
    if (activeSearchController === requestController) {
      activeSearchController = null;
      setSearchLoading(false);
    }
  }
}

   searchForm?.addEventListener("submit", (event) => {
  event.preventDefault();

  cancelAutomaticSearch();
  searchProducts(searchInput?.value);
});

searchInput?.addEventListener("input", () => {
  cancelAutomaticSearch();
  hideMessage(searchMessage);

  const currentValue = String(
    searchInput.value || ""
  ).trim();

  const currentKey =
    normaliseSearchKey(currentValue);

  /*
   * Cancel the previous backend request as soon as the
   * user changes the phrase.
   */
  if (
    activeSearchController &&
    currentKey !== lastRenderedSearchKey
  ) {
    activeSearchController.abort();
    activeSearchController = null;
    setSearchLoading(false);
  }

  if (currentValue.length < 2) {
    return;
  }

  /*
   * The displayed result already belongs to this phrase.
   */
  if (currentKey === lastRenderedSearchKey) {
    return;
  }

  automaticSearchTimer = window.setTimeout(() => {
    automaticSearchTimer = null;
    searchProducts(searchInput.value);
  }, AUTO_SEARCH_DELAY);
});

    root
      .querySelectorAll("[data-search-example]")
      .forEach((button) => {
        button.addEventListener("click", () => {
          const example =
            button.dataset.searchExample || "";

          cancelAutomaticSearch();

if (searchInput) {
  searchInput.value = example;
}

searchProducts(example);
        });
      });

    resultsList?.addEventListener("change", (event) => {
      const optionSelect = event.target.closest(
        "[data-product-option-select]"
      );

      if (optionSelect) {
        const productElement = optionSelect.closest(
          "[data-search-product]"
        );

        if (productElement) {
          updateProductCard(productElement);
        }

        return;
      }

      const quantityInput = event.target.closest(
        "[data-result-quantity]"
      );

      if (quantityInput) {
        quantityInput.value =
          normaliseQuantity(quantityInput.value);
      }
    });

    resultsList?.addEventListener("click", (event) => {
      const quantityButton = event.target.closest(
        "[data-result-quantity-change]"
      );

      if (quantityButton) {
        const productElement = quantityButton.closest(
          "[data-search-product]"
        );

        const quantityInput =
          productElement?.querySelector(
            "[data-result-quantity]"
          );

        if (!quantityInput) return;

        const change = Number.parseInt(
          quantityButton.dataset.resultQuantityChange,
          10
        );

        quantityInput.value = normaliseQuantity(
          normaliseQuantity(quantityInput.value) +
            change
        );

        return;
      }

      const addButton = event.target.closest(
        "[data-add-product-variant]"
      );

      if (!addButton || addButton.disabled) return;

      const productElement = addButton.closest(
        "[data-search-product]"
      );

      const product =
        productElement?.quoteProductData;

      const variant =
        productElement?.selectedVariantData;

      if (!product || !variant) return;

      const quantityInput =
        productElement.querySelector(
          "[data-result-quantity]"
        );

      addVariantToQuote(
        product,
        variant,
        quantityInput?.value
      );

      if (quantityInput) {
        quantityInput.value = "1";
      }
    });

    quoteItems?.addEventListener("click", (event) => {
      const removeButton = event.target.closest(
        "[data-remove-quote-item]"
      );

      if (removeButton) {
        removeQuoteItem(
          removeButton.dataset.variantId
        );
        return;
      }

      const quantityButton = event.target.closest(
        "[data-quote-quantity-change]"
      );

      if (!quantityButton) return;

      const variantId =
        quantityButton.dataset.variantId;

      const item = findQuoteItem(variantId);

      if (!item) return;

      const change = Number.parseInt(
        quantityButton.dataset.quoteQuantityChange,
        10
      );

      updateQuoteItemQuantity(
        variantId,
        normaliseQuantity(item.qty) + change
      );
    });

    quoteItems?.addEventListener("change", (event) => {
      const input = event.target.closest(
        "[data-quote-item-quantity-input]"
      );

      if (!input) return;

      updateQuoteItemQuantity(
        input.dataset.variantId,
        input.value
      );
    });
function getQuoteFormPayload() {
  if (!submitForm) {
    return null;
  }

  const formData = new FormData(submitForm);

  return {
    customer: {
      company: String(
        formData.get("company") || ""
      ).trim(),

      firstName: String(
        formData.get("firstName") || ""
      ).trim(),

      lastName: String(
        formData.get("lastName") || ""
      ).trim(),

      email: String(
        formData.get("email") || ""
      ).trim(),

      phone: String(
        formData.get("phone") || ""
      ).trim(),
    },

    address: {
      address1: String(
        formData.get("address1") || ""
      ).trim(),

      address2: "",

      city: String(
        formData.get("city") || ""
      ).trim(),

      province: "",

      zip: String(
        formData.get("postcode") || ""
      ).trim(),

      country: "Australia",
    },

    notes: String(
      formData.get("notes") || ""
    ).trim(),

    items: quote.items.map((item) => ({
      variantId: String(item.variantId),
      qty: normaliseQuantity(item.qty),
    })),
  };
}

function validateQuoteForm(payload) {
  if (!payload.items.length) {
    return {
      valid: false,
      message: "Add at least one product to your quote.",
    };
  }

  if (!payload.customer.firstName) {
    return {
      valid: false,
      message: "Enter your first name.",
      fieldName: "firstName",
    };
  }

  if (!payload.customer.lastName) {
    return {
      valid: false,
      message: "Enter your last name.",
      fieldName: "lastName",
    };
  }

  if (!payload.customer.email) {
    return {
      valid: false,
      message: "Enter your email address.",
      fieldName: "email",
    };
  }

  const emailIsValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      payload.customer.email
    );

  if (!emailIsValid) {
    return {
      valid: false,
      message: "Enter a valid email address.",
      fieldName: "email",
    };
  }



  return {
    valid: true,
  };
}

function focusInvalidField(fieldName) {
  if (!submitForm || !fieldName) return;

  const field = submitForm.elements.namedItem(
    fieldName
  );

  if (!(field instanceof HTMLElement)) {
    return;
  }

  field.setAttribute("aria-invalid", "true");
  field.focus();

  field.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

function clearInvalidFields() {
  if (!submitForm) return;

  submitForm
    .querySelectorAll('[aria-invalid="true"]')
    .forEach((field) => {
      field.removeAttribute("aria-invalid");
    });
}

function setSubmitLoading(loading) {
  isSubmitting = loading;

  if (submitButton) {
    submitButton.disabled =
      loading || quote.items.length === 0;
  }

  if (submitButtonText) {
    submitButtonText.textContent = loading
      ? "Submitting quote..."
      : "Submit quote request";
  }
}

async function submitQuoteRequest() {
  hideMessage(submitMessage);
  clearInvalidFields();

  const payload = getQuoteFormPayload();

  if (!payload) {
    showMessage(submitMessage, "The quote form could not be loaded.", "error");
    return;
  }

  const validation = validateQuoteForm(payload);

  if (!validation.valid) {
    showMessage(submitMessage, validation.message, "error");
    focusInvalidField(validation.fieldName);
    return;
  }

  if (!submitEndpoint) {
    showMessage(submitMessage, "The quote submission endpoint is not configured.", "error");
    return;
  }

  // Same ID on the automatic retry, so the server never creates the quote twice.
  payload.requestId =
    window.crypto && typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Sent as text/plain so the browser skips the CORS permission check,
  // which some hospital and corporate networks block.
  const send = async () => {
    const response = await fetch(submitEndpoint, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => null);
    return { response, result };
  };

  setSubmitLoading(true);

  try {
    let attempt;

    try {
      attempt = await send();
    } catch (connectionError) {
      // Connection dropped: wait a moment and try once more.
      await new Promise((resolve) => setTimeout(resolve, 2000));
      attempt = await send();
    }

    const { response, result } = attempt;

    if (!response.ok || !result?.ok) {
      const error = new Error(
        result?.message || result?.error || `Quote submission failed with status ${response.status}.`
      );
      error.code = result?.error;
      throw error;
    }

    quote.items = [];
    saveAndRenderQuote();

    const notesField = submitForm?.elements.namedItem("notes");

    if (notesField instanceof HTMLTextAreaElement) {
      notesField.value = "";
    }

    showMessage(
      submitMessage,
      `Thank you! Your quote request ${result.quoteNo} has been received. We are emailing your quote to ${result.sentTo} now.`,
      "success"
    );

    submitMessage?.scrollIntoView({ behavior: "smooth", block: "center" });
  } catch (error) {
    console.error("Quote submission failed:", error);

    const knownErrors = {
      items_required: "Add at least one product to your quote.",
      valid_customer_email_required: "Enter a valid email address.",
    };

    showMessage(
      submitMessage,
      knownErrors[error.code] ||
        (error instanceof TypeError
          ? "We couldn't reach our quote system from your network. Please call 1300 721 733 or email info@medisa.com.au and we'll prepare your quote."
          : "Sorry, your quote could not be sent. Please try again in a minute, or call 1300 721 733."),
      "error"
    );
  } finally {
    setSubmitLoading(false);
    renderQuote();
  }
}

submitForm?.addEventListener(
  "submit",
  (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    submitQuoteRequest();
  }
);


submitForm?.addEventListener(
  "input",
  (event) => {
    const field = event.target;

    if (!(field instanceof HTMLElement)) {
      return;
    }

    field.removeAttribute("aria-invalid");

    if (
      submitMessage?.classList.contains(
        "is-error"
      )
    ) {
      hideMessage(submitMessage);
    }
  }
);


    clearButton?.addEventListener("click", () => {
      if (quote.items.length === 0) return;

      if (
        !window.confirm(
          "Remove all products from this quote?"
        )
      ) {
        return;
      }

      quote.items = [];
      saveAndRenderQuote();
    });

    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;

      quote = loadQuote();
      renderQuote();
    });

    window.addEventListener(
      "medisa:quote-updated",
      (event) => {
        if (
          !event.detail ||
          !Array.isArray(event.detail.items)
        ) {
          return;
        }

        quote = event.detail;
        renderQuote();
      }
    );

    renderQuote();
  }

  function initialiseAllQuotePages(
    container = document
  ) {
    container
      .querySelectorAll(".md-quote-page")
      .forEach(initialiseQuotePage);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialiseAllQuotePages()
    );
  } else {
    initialiseAllQuotePages();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialiseAllQuotePages(event.target);
    }
  );
})();